import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, Loader2, Pencil, Sparkles } from "lucide-react";
import { BaseSheet } from "@/components/common/BaseSheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  GENIE_DOCUMENT_TEMPLATES,
  GenieDocumentSuiteProposal,
} from "@/types/genie-documents/genie-document";

interface GenieDocumentSuiteReviewSheetProps {
  isOpen: boolean;
  onClose: () => void;
  proposals: GenieDocumentSuiteProposal[];
  isGenerating: boolean;
  onGenerate: (approved: GenieDocumentSuiteProposal[]) => void;
}

const TEMPLATE_LABEL: Record<string, string> = Object.fromEntries(
  GENIE_DOCUMENT_TEMPLATES.map((t) => [t.key, t.label]),
);

const PRIMARY_CTA =
  "bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold shadow-lg shadow-genie-primary/30 transition-all disabled:opacity-50";

/**
 * The review step of the suite — the planner's proposals as a checklist. Untick
 * what you don't want, rename anything inline, then "Create N documents" hands
 * the approved set to the swarm. Every row starts ticked: the default path is
 * click-click-done.
 */
export const GenieDocumentSuiteReviewSheet = ({
  isOpen,
  onClose,
  proposals,
  isGenerating,
  onGenerate,
}: GenieDocumentSuiteReviewSheetProps) => {
  const [selected, setSelected] = useState<boolean[]>([]);
  const [titles, setTitles] = useState<string[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Re-seed whenever a fresh set of proposals arrives.
  useEffect(() => {
    if (!isOpen) return;
    setSelected(proposals.map(() => true));
    setTitles(proposals.map((p) => p.title));
    setEditingIndex(null);
  }, [isOpen, proposals]);

  const approved = useMemo(
    () =>
      proposals
        .map((p, i) => ({ ...p, title: titles[i]?.trim() || p.title }))
        .filter((_, i) => selected[i]),
    [proposals, titles, selected],
  );

  const toggle = (index: number) =>
    setSelected((prev) => prev.map((value, i) => (i === index ? !value : value)));

  const setTitle = (index: number, title: string) =>
    setTitles((prev) => prev.map((value, i) => (i === index ? title : value)));

  return (
    <BaseSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Your document suite"
      description="Here's what your Genie recommends creating from its knowledge. Untick anything you don't want, then create the rest in one go."
      size="lg"
      preventOutsideClick={isGenerating}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            {approved.length} of {proposals.length} selected
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={isGenerating}>
              Cancel
            </Button>
            <Button
              onClick={() => onGenerate(approved)}
              disabled={approved.length === 0 || isGenerating}
              className={PRIMARY_CTA}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Drafting your suite…
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Create {approved.length} document{approved.length === 1 ? "" : "s"}
                </>
              )}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3 px-6 py-6 sm:px-8">
        {proposals.map((proposal, index) => {
          const active = selected[index];
          const isEditing = editingIndex === index;
          return (
            <div
              key={`${proposal.title}-${index}`}
              className={cn(
                "flex items-start gap-3 rounded-xl border-2 p-4 transition-all",
                active ? "border-genie-primary bg-genie-primary/5" : "border-gray-200 bg-white",
              )}
            >
              <button
                type="button"
                onClick={() => toggle(index)}
                className="mt-0.5 shrink-0"
                aria-label={active ? "Remove from suite" : "Include in suite"}
              >
                <Checkbox checked={active} className="pointer-events-none" />
              </button>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {isEditing ? (
                    <Input
                      value={titles[index] ?? proposal.title}
                      onChange={(event) => setTitle(index, event.target.value)}
                      onBlur={() => setEditingIndex(null)}
                      onKeyDown={(event) => event.key === "Enter" && setEditingIndex(null)}
                      autoFocus
                      className="h-8 text-base sm:text-sm"
                    />
                  ) : (
                    <p className={cn("font-semibold", active ? "text-gray-900" : "text-gray-500")}>
                      {titles[index]?.trim() || proposal.title}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditingIndex(isEditing ? null : index)}
                    className="shrink-0 text-gray-400 transition-colors hover:text-genie-primary"
                    aria-label="Rename document"
                  >
                    {isEditing ? <Check className="h-4 w-4" /> : <Pencil className="h-3.5 w-3.5" />}
                  </button>
                </div>

                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-genie-primary/10 px-2 py-0.5 text-[11px] font-semibold text-genie-primary">
                    {TEMPLATE_LABEL[proposal.template] ?? proposal.template}
                  </span>
                  {proposal.documentIds.length === 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-amber-600">
                      <AlertTriangle className="h-3 w-3" />
                      Draws from all sources
                    </span>
                  )}
                </div>

                <p className="mt-2 text-sm leading-snug text-gray-600">{proposal.why}</p>
              </div>
            </div>
          );
        })}
      </div>
    </BaseSheet>
  );
};

export default GenieDocumentSuiteReviewSheet;
